import { useEffect, useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useIncidents } from '@/hooks/useIncidents';
import { useDeviceTrafficSeries } from '@/hooks/useDeviceTrafficSeries';
import { buildRows, hasQuantity, summariseCapacity } from './rows';
import { denominatorNote } from './capacity';
import { SYNC_ID } from './chart';
import { formatBucket } from './format';
import { RangeControl } from './RangeControl';
import { RANGE_MS, refreshMs, type TrafficRange } from './range';
import { VerdictStrip } from './VerdictStrip';
import { TrafficMirror } from './TrafficMirror';
import { FaultFacets } from './FaultFacets';
import { EventRail } from './EventRail';

/**
 * What one port carried, and what went wrong on it.
 *
 * The thinnest consumer of the shared block set — one port, no far end, nothing
 * surface-specific. That is the point: if this section needed a block of its
 * own, the block set would be wrong, and it is cheap to find out here rather
 * than after the link and node panels are built on it.
 *
 * Every block shares one recharts `syncId`, so one crosshair moves across all of
 * them and the reader can see that a discard burst sits on a traffic knee.
 *
 * The time range is one control for the whole section, in the filter row above
 * everything it scopes — never per block and never inside a chart card. It is
 * accepted as a prop so the Views page can lift it into the page filter row
 * without this component changing shape; unsupplied, the section owns it.
 */
export function InterfaceTrafficSection({
  deviceId,
  interfaceId,
  interfaceName,
  interfaceType,
  range: controlledRange,
  onRangeChange,
}: {
  deviceId: string;
  /** The interface, by id: its series are found by its name (gateway#102). */
  interfaceId: string;
  interfaceName: string;
  interfaceType: string | null;
  range?: TrafficRange;
  onRangeChange?: (range: TrafficRange) => void;
}) {
  const [localRange, setLocalRange] = useState<TrafficRange>('1h');
  const range = controlledRange ?? localRange;
  const setRange = onRangeChange ?? setLocalRange;
  const [unit, setUnit] = useState<'bits' | 'percent'>('bits');

  // The window's end advances on a timer rather than on every render, so the
  // query key changes only when the window genuinely moves.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Tied to the range: a 7-day chart re-fetching every minute spends a
    // megabyte to move its right edge by a pixel.
    const id = setInterval(() => { setNow(Date.now()); }, refreshMs(range));
    return () => { clearInterval(id); };
  }, [range]);

  const bounds = useMemo(() => ({ start: now - RANGE_MS[range], end: now }), [now, range]);
  const { data, isLoading, isError } = useDeviceTrafficSeries(deviceId, bounds, interfaceId);

  const { user } = useAuth();
  const { data: incidents } = useIncidents(user?.organizationId ?? undefined, { deviceId });

  const iface = data?.interfaces.find((row) => row.interfaceId === interfaceId);
  const rows = useMemo(() => (iface ? buildRows(iface) : []), [iface]);
  const capacity = useMemo(() => summariseCapacity(iface?.capacity ?? []), [iface]);
  const note = denominatorNote(capacity, interfaceType);

  // Never silently left on a unit the port cannot express: if the reader had
  // percentages and the denominator went away, the toggle returns to bits
  // rather than drawing an empty plot.
  if (!note.available && unit !== 'bits') setUnit('bits');

  const caption = [
    data?.bucketSeconds !== null && data?.bucketSeconds !== undefined && data.bucketFunction
      ? `Each point is the ${data.bucketFunction === 'min' ? 'lowest' : 'highest'} reading in ${formatBucket(data.bucketSeconds)}.`
      : null,
    note.caption,
  ]
    .filter((part): part is string => part !== null)
    .join(' ');

  return (
    <section className="bg-card border border-border rounded-lg p-5 mt-4">
      <header className="mb-1 flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Activity className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          Traffic on {interfaceName}
        </h2>
        <p className="text-[11px] text-muted-foreground">
          above the line: into this port · below: out of it
        </p>
      </header>

      <div className="flex items-center gap-2 flex-wrap mt-3 mb-4">
        <RangeControl value={range} onChange={setRange} />
        <Segmented
          options={[
            { value: 'bits' as const, label: 'bits/s' },
            { value: 'percent' as const, label: '% of line rate', disabled: !note.available },
          ]}
          value={unit}
          onChange={setUnit}
          ariaLabel="Units"
          disabledReason={note.reason}
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">
          The metric store did not answer. The port itself is unaffected.
        </p>
      ) : !iface ? (
        <p className="text-sm text-muted-foreground">
          No readings arrived for this port in this window. That is a collection gap rather than a
          quiet port — an interface that carried nothing still reports zeros.
        </p>
      ) : (
        <div className="space-y-5">
          <VerdictStrip verdict={iface.verdict} />
          <TrafficMirror
            rows={rows}
            start={bounds.start}
            end={bounds.end}
            syncId={SYNC_ID}
            capacity={capacity}
            caption={caption}
            mode={unit}
          />
          <EventRail
            start={bounds.start}
            end={bounds.end}
            syncId={SYNC_ID}
            statusBefore={iface.statusBefore}
            transitions={iface.transitions}
            incidents={incidents ?? []}
          />
          <FaultFacets
            rows={rows}
            start={bounds.start}
            end={bounds.end}
            syncId={SYNC_ID}
            hasErrors={hasQuantity(iface, 'errors_in') || hasQuantity(iface, 'errors_out')}
            hasDiscards={hasQuantity(iface, 'discards_in') || hasQuantity(iface, 'discards_out')}
            hasPacketMix={hasQuantity(iface, 'nonunicast_in') && hasQuantity(iface, 'packets_in')}
          />
        </div>
      )}
    </section>
  );
}

/**
 * A control the reader can see is off, and see why.
 *
 * The unit toggle is disabled rather than hidden when no denominator resolves:
 * hiding it makes the absence invisible, and the absence is itself the finding
 * for more than half the fleet.
 */
function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  disabledReason,
}: {
  options: { value: T; label: string; disabled?: boolean }[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  disabledReason?: string | null;
}) {
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={option.disabled}
          title={option.disabled ? (disabledReason ?? undefined) : undefined}
          onClick={() => { onChange(option.value); }}
          aria-pressed={value === option.value}
          className={cn(
            'px-2.5 py-1 text-xs border-r border-border last:border-r-0',
            value === option.value ? 'bg-muted text-foreground font-medium' : 'text-muted-foreground hover:text-foreground',
            option.disabled && 'opacity-40 cursor-not-allowed hover:text-muted-foreground',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
